import React, { useCallback, useEffect, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";

import { api } from "./api";
import Campana from "./Campana";
import { Bar, Btn, Panel, Strip, money, num, pct } from "./ui";

const COLOR_HALLAZGO = {
  pierde: "#D92D20",
  gancho_sin_venta: "#CA8A04",
  fatiga: "#CA8A04",
  apagado_que_rendia: "#0369A1",
};

const ETIQUETA = {
  pierde: "PIERDE",
  gancho_sin_venta: "GANCHO SIN VENTA",
  fatiga: "FATIGA",
  apagado_que_rendia: "APAGADO QUE RENDÍA",
};

/**
 * Rendimiento por creativo (no por anuncio) o por campaña + hallazgos + techo de aprendizaje.
 * Los números salen del mismo servicio que `php artisan meta:analizar`.
 *
 * Arranca en 7 días: la pregunta que se hace todos los días es "cómo viene ahora", y una
 * ventana larga la contesta mal porque promedia contra semanas que ya no existen. Para mirar
 * histórico están los otros botones.
 *
 * Las dos agrupaciones vienen en la misma respuesta, así que cambiar de nivel no vuelve a
 * pedir nada: es la misma lectura mirada de dos maneras y los totales cierran entre sí.
 */
export default function Rendimiento() {
  const [dias, setDias] = useState(7);
  const [nivel, setNivel] = useState("creativo");
  const [campanaId, setCampanaId] = useState(null);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [expandido, setExpandido] = useState(null);

  const cargar = useCallback((d) => {
    setDatos(null);
    setError(null);
    api.rendimiento(d).then(setDatos).catch((e) => setError(e.message));
  }, []);

  useEffect(() => cargar(dias), [dias, cargar]);

  /*
   * El selector de días vive acá arriba y no adentro de cada vista: es el mismo rango para
   * todo, incluido el detalle de una campaña. Poder cambiarlo sin salir del detalle es lo que
   * permite pasar de "cómo viene esta semana" a "cómo viene el trimestre" sin perder de vista
   * qué campaña se está mirando.
   */
  const barra = (
    <Bar>
      {[7, 30, 90, 365].map((d) => (
        <Btn key={d} on={dias === d} onClick={() => setDias(d)}>{d}d</Btn>
      ))}
      {datos && (
        <span style={{ fontSize: 11.5, color: "#7A8699" }}>
          {datos.ventana.desde} → {datos.ventana.hasta} · {(datos.atribucion || []).join(",")} · mín {money(datos.umbral)}
        </span>
      )}
    </Bar>
  );

  if (campanaId) {
    return (
      <div>
        {barra}
        <Campana id={campanaId} dias={dias} onVolver={() => setCampanaId(null)} />
      </div>
    );
  }

  if (error) return <div>{barra}<Strip tone="error">{error}</Strip></div>;
  if (!datos) return <div>{barra}<CircularProgress size={22} /></div>;

  const ap = datos.aprendizaje;
  const campanas = datos.campanas || [];
  const cob = datos.cobertura;
  const vacia = datos.creativos.length === 0 && campanas.length === 0;
  // Tolera la forma vieja (lista pelada) por si el panel queda adelante del deploy de la API.
  const hallazgos = Array.isArray(datos.hallazgos)
    ? { items: datos.hallazgos, total: datos.hallazgos.length, omitidos: 0, por_tipo: null }
    : datos.hallazgos;
  // «pagos» = aperturas del bloque de pago, y «$/pago» su costo. Es el número con el que se
// maneja una campaña de presupuesto chico: el CPA tarda semanas en juntar muestra.
  const cols = ["creativo", "campaña", "copias", "gasto", "pagos", "$/pago", "compras", "ROAS", "CPA", "CTR", "enganche", "freq", ""];
  /*
   * La vista por campaña no repite las columnas de creativo: agrega las que sólo existen a
   * ese nivel («días» con entrega, cuántos conjuntos y cuántos creativos adentro), que son
   * las que explican si un promedio de campaña representa algo.
   */
  const colsCampana = ["campaña", "días", "conj.", "creat.", "gasto", "pagos", "$/pago", "compras", "ingreso", "ROAS", "CPA", "CTR", "CPM", "freq", ""];

  return (
    <div>
      {barra}

      {/*
        Sin este aviso, pedir 365 días con 8 cargados devuelve lo mismo que pedir 30 y parece
        que el filtro está roto. El dato es de la tabla, no de la ventana pedida.
      */}
      {cob && cob.parcial && (
        <Strip tone="warn">
          Pediste desde <b>{datos.ventana.desde}</b>, pero sólo hay datos cargados desde{" "}
          <b>{cob.primero}</b> ({cob.dias} día(s)). La tabla muestra lo que
          hay, así que este rango da lo mismo que uno más corto. Para traer el histórico:{" "}
          <code>php artisan meta:import-ads --days=365</code>
        </Strip>
      )}

      {/*
        El otro extremo: la tabla no llega hasta hoy. Es el aviso que más falta hace en una
        ventana corta, porque ahí el resultado no es "números viejos" sino CERO filas, y una
        pantalla vacía se lee como un bug del panel en vez de como un import que no corrió.
      */}
      {cob && cob.desactualizada && (
        <Strip tone={vacia ? "error" : "warn"}>
          El último día importado es <b>{cob.ultimo}</b> (hace {cob.ultimo_dia_faltante} día(s)).{" "}
          {vacia
            ? <>Por eso esta ventana no muestra nada: no hay una sola fila entre {datos.ventana.desde} y {datos.ventana.hasta}. No es que no se haya gastado.</>
            : <>Los últimos {cob.ultimo_dia_faltante} día(s) de la ventana están vacíos y bajan todos los promedios.</>}
          {" "}Para ponerla al día: <code>php artisan meta:import-ads --days={Math.max(3, (cob.ultimo_dia_faltante || 0) + 1)}</code>
        </Strip>
      )}

      {/* Techo estructural: una línea, no un bloque entero. */}
      <Strip tone={ap.llegan === 0 ? "warn" : "ok"}>
        <b>Aprendizaje:</b> {ap.conversiones} conversiones en {ap.dias} días sobre <b>{ap.adsets}</b> conjuntos
        (Meta pide ~{ap.necesarias} por conjunto).{" "}
        {ap.llegan === 0 ? (
          <>Ninguno llega: este volumen sostiene <b>{ap.capacidad}</b>. La salida es consolidar, no esperar.</>
        ) : (
          <>{ap.llegan} conjunto(s) llegan al umbral.</>
        )}
      </Strip>

      {/*
        Ordenados por plata en juego y con tope. A 365 días esto daba ~175 avisos y la lista
        dejaba de servir; los que no entran se cuentan en vez de desaparecer en silencio.
      */}
      {hallazgos.items.length > 0 && (
        <Panel
          title={`Hallazgos (${hallazgos.items.length}${hallazgos.omitidos ? ` de ${hallazgos.total}` : ""})`}
          right={
            <span style={{ fontSize: 11, color: "#7A8699", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>
              ordenados por plata en juego
            </span>
          }
          pad={0}
        >
          {hallazgos.items.map((h, i) => (
            <div
              key={i}
              style={{
                display: "flex", gap: 8, alignItems: "baseline",
                padding: "5px 10px",
                borderBottom: i < hallazgos.items.length - 1 ? "1px solid #F2F5FA" : "none",
                fontSize: 12.5,
              }}
            >
              <span style={{ color: COLOR_HALLAZGO[h.tipo] || "#334155", fontWeight: 800, fontSize: 10.5, minWidth: 128 }}>
                {ETIQUETA[h.tipo] || h.tipo}
              </span>
              <span style={{ flex: 1 }}>
                <b>{h.creativo}</b> — {h.detalle}
                <span style={{ color: "#A0AABA", marginLeft: 6 }}>({h.umbral})</span>
              </span>
              <span style={{ fontWeight: 700, color: "#46536B", whiteSpace: "nowrap" }}>
                {money(h.impacto)}
              </span>
            </div>
          ))}

          {hallazgos.omitidos > 0 && (
            <div style={{ padding: "5px 10px", fontSize: 11.5, color: "#7A8699", background: "#FBFCFE" }}>
              … y {hallazgos.omitidos} hallazgo(s) más de menor impacto.
              {hallazgos.por_tipo && (
                <> Total por tipo: {Object.keys(hallazgos.por_tipo).map((t) => `${ETIQUETA[t] || t} ${hallazgos.por_tipo[t]}`).join(" · ")}.</>
              )}
            </div>
          )}
        </Panel>
      )}

      <Panel
        title={nivel === "creativo" ? "Rendimiento por creativo" : "Rendimiento por campaña"}
        right={
          <>
            {/*
              El mismo dato tiene dos lecturas y las dos hacen falta: por creativo se decide
              QUÉ pieza sirve (y por eso agrupa por video, porque las copias se re-suben), y
              por campaña se decide DÓNDE va el presupuesto. Un creativo repartido entre cinco
              campañas no se puede juzgar desde la vista de campaña, y una campaña no se puede
              juzgar sumando creativos que además corren en otras.
            */}
            <Btn on={nivel === "creativo"} onClick={() => setNivel("creativo")}>creativo</Btn>
            <Btn on={nivel === "campana"} onClick={() => setNivel("campana")}>campaña</Btn>
            <span style={{ fontSize: 11, color: "#7A8699", textTransform: "none", letterSpacing: 0, fontWeight: 400, marginLeft: 4 }}>
              {nivel === "creativo"
                ? `agrupado por video/imagen · ${datos.creativos.length} juzgables`
                : `${campanas.length} con entrega · sin filtro de gasto mínimo`}
            </span>
          </>
        }
        pad={0}
      >
        {/* overflow en los dos ejes: con la columna de campaña la tabla puede no entrar a lo ancho. */}
        <div style={{ maxHeight: "calc(100vh - 300px)", overflow: "auto" }}>
          {nivel === "campana" ? (
            <table>
              <thead>
                <tr>
                  {colsCampana.map((c, i) => (
                    <th key={c || i} style={{ textAlign: i === 0 ? "left" : "right" }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {campanas.map((c) => (
                  <tr key={c.clave}>
                    <td style={{ maxWidth: 320 }}>
                      <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={c.nombre}>
                        {c.nombre}
                        {!c.corriendo && <span style={{ fontSize: 10, color: "#A0AABA", fontWeight: 400 }}> · apagada</span>}
                      </div>
                      <div style={{ fontSize: 10.5, color: "#A0AABA" }}>{c.clave}</div>
                    </td>
                    {/*
                      Días CON entrega, no días de la ventana: una campaña que corrió 8 de 30
                      días no se compara con una que corrió los 30 sin este número a la vista.
                    */}
                    <td style={{ textAlign: "right" }}>{c.dias}</td>
                    <td style={{ textAlign: "right" }}>{c.adsets}</td>
                    <td style={{ textAlign: "right" }}>{c.creativos}</td>
                    <td style={{ textAlign: "right" }}>{money(c.gasto)}</td>
                    <td style={{ textAlign: "right" }}>{c.pagos_iniciados || 0}</td>
                    <td style={{ textAlign: "right" }}>{money(c.costo_pago_iniciado)}</td>
                    <td style={{ textAlign: "right" }}>{c.compras}</td>
                    <td style={{ textAlign: "right" }}>{money(c.valor)}</td>
                    <td
                      style={{
                        textAlign: "right", fontWeight: 800,
                        color: c.roas >= 1.5 ? "#16A34A" : c.roas < 0.9 ? "#D92D20" : "#24303F",
                      }}
                    >
                      {Number(c.roas).toFixed(2)}
                    </td>
                    <td style={{ textAlign: "right" }}>{money(c.cpa)}</td>
                    <td style={{ textAlign: "right" }}>{pct(c.ctr)}</td>
                    <td style={{ textAlign: "right" }}>{money(c.cpm)}</td>
                    <td style={{ textAlign: "right" }}>{Number(c.frecuencia_max).toFixed(2)}</td>
                    <td style={{ textAlign: "right" }}>
                      <Btn tone="pri" onClick={() => setCampanaId(c.clave)}>analizar</Btn>
                    </td>
                  </tr>
                ))}

                {campanas.length > 1 && (
                  <tr style={{ background: "#F7F9FC", fontWeight: 700 }}>
                    <td>Total ({campanas.length} campañas)</td>
                    <td colSpan={3} />
                    <td style={{ textAlign: "right" }}>{money(campanas.reduce((s, c) => s + c.gasto, 0))}</td>
                    <td style={{ textAlign: "right" }}>{num(campanas.reduce((s, c) => s + (c.pagos_iniciados || 0), 0))}</td>
                    <td />
                    <td style={{ textAlign: "right" }}>{num(campanas.reduce((s, c) => s + c.compras, 0))}</td>
                    <td style={{ textAlign: "right" }}>{money(campanas.reduce((s, c) => s + c.valor, 0))}</td>
                    <td style={{ textAlign: "right" }}>
                      {(campanas.reduce((s, c) => s + c.valor, 0) / Math.max(1, campanas.reduce((s, c) => s + c.gasto, 0))).toFixed(2)}
                    </td>
                    <td colSpan={5} />
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
          <table>
            <thead>
              <tr>
                {/* creativo y campaña son texto: van a la izquierda. El resto son números. */}
                {cols.map((c, i) => (
                  <th key={c || i} style={{ textAlign: i <= 1 ? "left" : "right" }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datos.creativos.map((c) => (
                <React.Fragment key={c.clave}>
                  <tr>
                    <td style={{ maxWidth: 300 }}>
                      <div style={{ fontWeight: 600 }}>
                        {c.nombre}
                        {!c.corriendo && <span style={{ fontSize: 10, color: "#A0AABA", fontWeight: 400 }}> · apagado</span>}
                      </div>
                      <div style={{ fontSize: 10.5, color: "#A0AABA" }}>{c.clave}</div>
                    </td>

                    {/*
                      La campaña DOMINANTE por gasto, no "su" campaña: las copias de un creativo
                      se reparten entre varias. El +N avisa en cuántas más está, y el detalle
                      completo sale al expandir.
                    */}
                    <td style={{ maxWidth: 190 }}>
                      <div
                        title={c.campana_principal || ""}
                        style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: 12 }}
                      >
                        {c.campana_principal || "—"}
                      </div>
                      {c.campanas_total > 1 && (
                        <div style={{ fontSize: 10.5, color: "#8A6D1F", fontWeight: 700 }}>
                          +{c.campanas_total - 1} campaña{c.campanas_total - 1 > 1 ? "s" : ""} más
                        </div>
                      )}
                    </td>

                    <td style={{ textAlign: "right" }}>{c.copias}</td>
                    <td style={{ textAlign: "right" }}>{money(c.gasto)}</td>
                    <td style={{ textAlign: "right" }}>{c.pagos_iniciados || 0}</td>
                    <td style={{ textAlign: "right" }}>{money(c.costo_pago_iniciado)}</td>
                    <td style={{ textAlign: "right" }}>{c.compras}</td>
                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: 800,
                        color: c.roas >= 1.5 ? "#16A34A" : c.roas < 0.9 ? "#D92D20" : "#24303F",
                      }}
                    >
                      {Number(c.roas).toFixed(2)}
                    </td>
                    <td style={{ textAlign: "right" }}>{money(c.cpa)}</td>
                    <td style={{ textAlign: "right" }}>{Number(c.ctr).toFixed(2)}%</td>
                    <td style={{ textAlign: "right" }}>{Number(c.enganche).toFixed(1)}%</td>
                    <td style={{ textAlign: "right" }}>{Number(c.frecuencia_max).toFixed(2)}</td>
                    <td style={{ textAlign: "right" }}>
                      <Btn onClick={() => setExpandido(expandido === c.clave ? null : c.clave)}>
                        {expandido === c.clave ? "−" : "detalle"}
                      </Btn>
                    </td>
                  </tr>

                  {expandido === c.clave && (
                    <tr>
                      <td colSpan={11} style={{ background: "#F7F9FC", padding: "8px 10px !important" }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#7A8699", letterSpacing: ".04em", marginBottom: 4 }}>
                          CAMPAÑAS EN LAS QUE CORRIÓ <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— clic para analizarla</span>
                        </div>
                        {/*
                          Desde acá se llega al análisis de la campaña sin volver a la tabla y
                          cambiar de nivel: mirando un creativo repartido entre cinco campañas,
                          la pregunta que sigue casi siempre es "¿y en cuál de todas anda mal?".
                        */}
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                          {Object.keys(c.campanas).map((cid) => (
                            <button
                              key={cid}
                              onClick={() => setCampanaId(cid)}
                              title="Ver el análisis de esta campaña"
                              style={{
                                background: "#fff", border: "1px solid #E3E8F0", borderRadius: 5,
                                padding: "4px 8px", fontSize: 11.5, maxWidth: 260,
                                textAlign: "left", cursor: "pointer", font: "inherit", color: "#24303F",
                              }}
                            >
                              <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {c.campanas[cid].nombre}
                              </div>
                              <div style={{ color: "#7A8699" }}>
                                {money(c.campanas[cid].gasto)} · {c.campanas[cid].compras} cmp · CPA {money(c.campanas[cid].cpa)}
                              </div>
                            </button>
                          ))}
                        </div>

                        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#7A8699", letterSpacing: ".04em", marginBottom: 4 }}>
                          POR PAÍS
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {Object.keys(c.paises).map((iso) => (
                            <div
                              key={iso}
                              style={{
                                background: "#fff", border: "1px solid #E3E8F0", borderRadius: 5,
                                padding: "4px 8px", fontSize: 11.5, minWidth: 96,
                              }}
                            >
                              <b>{iso}</b> · {money(c.paises[iso].gasto)}
                              <div style={{ color: "#7A8699" }}>
                                {c.paises[iso].compras} cmp · CPA {money(c.paises[iso].cpa)}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ fontSize: 11, color: "#A0AABA", marginTop: 5 }}>
                          El mismo creativo rinde muy distinto por mercado: el CPA objetivo sale del LTV de cada país.
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </Panel>
    </div>
  );
}
